import { useEffect, useState } from 'react';

import { listTenants } from '../api/tenants';

/**
 * Resolve tenant ids → display names for the superadmin diagnostic
 * pages (System logs, Audit trail). Those rows only carry `tenant_id`;
 * this fetches the tenant roster once (including soft-deleted tenants,
 * since old log rows can reference them) and returns a lookup.
 *
 * Non-fatal on failure — the caller falls back to a shortened id so the
 * page still renders if the roster fetch is blocked.
 */
export function useTenantNames(
  enabled = true,
): (id: string | null | undefined) => string | null {
  const [names, setNames] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    // Skip the (superadmin-only) roster fetch on tenant-scoped surfaces —
    // a tenant_admin can't list all tenants, and doesn't need to: the
    // single-tenant audit view hides the tenant column anyway.
    if (!enabled) return;
    let alive = true;
    void (async () => {
      try {
        const tenants = await listTenants(true);
        if (!alive) return;
        setNames(new Map(tenants.map((t) => [t.id, t.name])));
      } catch {
        // Leave the map empty; callers fall back to the raw id.
      }
    })();
    return () => {
      alive = false;
    };
  }, [enabled]);

  return (id) => (id ? names.get(id) ?? null : null);
}
